import { logger } from '@adornis/base/logging.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { expr_eq, expr_in } from '@adornis/buchungsheld-db/util.js';
import { AggregatedEntity } from './entities/aggregated-entity.js';

// Extend the baseqlMetaData type to include our custom metadata
declare module '@adornis/baseql/metadata/metaDataStore.js' {
  interface BaseQLMetadata {
    customMetadata: {
      aggregatedFields?: AggregatedFieldStage[];
      [key: string]: unknown;
    };
  }
}

export interface AggregatedFieldOptions {
  lookupFrom: string;
  localField: string;
  foreignField: string;
}

export interface AggregatedFieldStage {
  entityClass: string;
  fieldName: string;
  isArray: boolean;
  lookupStage: {
    $lookup: {
      from: string;
      localField: string;
      foreignField: string;
      as: string;
    };
  };
}

export function AggregatedField(typeFunction: (type: any) => any, lookupConstructor: () => any) {
  return function (target: any, propertyKey: string) {
    // Get the constructor of the class
    const constructor = target.constructor as unknown as { _class?: string; prototype: any };

    // Make sure we're dealing with an AggregatedEntity subclass
    if (!(constructor.prototype instanceof AggregatedEntity)) {
      logger.error(
        `AggregatedField can only be used on AggregatedEntity subclasses. ${
          constructor.constructor?.name || 'Unknown'
        } is not an AggregatedEntity.`,
      );
      return;
    }

    // Get the class name
    const className = constructor._class;
    if (!className) {
      logger.error(`Class ${constructor.constructor?.name || 'Unknown'} does not have a _class property.`);
      return;
    }

    // Initialize customMetadata if it doesn't exist
    if (!baseqlMetaData.customMetadata) {
      baseqlMetaData.customMetadata = {};
    }

    // Initialize the aggregated fields metadata if it doesn't exist
    if (!baseqlMetaData.customMetadata.aggregatedFields) {
      baseqlMetaData.customMetadata.aggregatedFields = [] as AggregatedFieldStage[];
    }

    // Create the lookup stage
    const propertyType = typeFunction(void 0);
    const [fieldType, fieldTypeIsArray] = (() => {
      if (Array.isArray(propertyType)) return [propertyType[0], true];
      return [propertyType, false];
    })();

    const lookupStage: AggregatedFieldStage = {
      entityClass: className,
      isArray: fieldTypeIsArray,
      fieldName: propertyKey,
      lookupStage: ((constructor, gqlFields: BaseQLSelectionSet) =>
        ({
          $lookup: (() => {
            const propertyGqlFields = gqlFields[propertyKey];
            if (!propertyGqlFields || Object.keys(propertyGqlFields).length === 0) {
              return null;
            }

            const _lookup = lookupConstructor.bind(constructor)();
            if ('pipeline' in _lookup) {
              const lookupStage = {
                from: fieldType._collectionName,
                as: propertyKey,
                ..._lookup,
              };
              lookupStage.pipeline.push({ $project: propertyGqlFields });
              return lookupStage;
            }

            if (!('localField' in _lookup)) throw Error("can't construct lookup stage, localField is required");
            const { localField } = _lookup;

            // check weather localField is an array
            const aggregatedEntityNode = baseqlMetaData.entities.find(entity => entity._class === className);
            const aggregatedEntityType = aggregatedEntityNode?.type();
            const localFieldDef = (aggregatedEntityType as any)?.fields[localField];
            if (!localFieldDef) throw new Error(`localField ${localField} not found in entity ${className}`);
            const localFieldArrayDepth = localFieldDef.arrayDepth();
            if (localFieldArrayDepth > 1)
              throw new Error(`localField ${localField} has depth ${localFieldArrayDepth} which is not supported`);
            const localFieldIsArray = localFieldArrayDepth === 1;

            // Use expr_in for array fields, expr_eq for scalar fields
            const matchExpr = localFieldIsArray ? expr_in('$_id', '$$localField') : expr_eq('$_id', '$$localField');

            return {
              from: fieldType._collectionName,
              as: propertyKey,
              let: { localField: '$' + localField },
              pipeline: [
                { $match: matchExpr }, //
                { $project: propertyGqlFields },
              ],
            };
          })(),
        } as any)) as any,
    };

    // Add the lookup stage to the metadata
    baseqlMetaData.customMetadata.aggregatedFields.push(lookupStage);
  };
}

export function getAggregatedFieldStages(entity: typeof AggregatedEntity): AggregatedFieldStage[] {
  let node = baseqlMetaData.entities.find(node => node._class === entity._class);
  const entityClasses = [] as string[];
  while (node) {
    entityClasses.push(node._class);
    node = node?.parent;
  }

  const allFieldStages = baseqlMetaData.customMetadata.aggregatedFields as AggregatedFieldStage[];
  return allFieldStages.filter(stage => entityClasses.includes(stage.entityClass));
}
